import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Save, PlugZap, Loader2, Link2 } from "lucide-react";

export default function ConfigPanel({ config, onSaved }) {
  const [url, setUrl] = useState(config?.xtream_url || "");
  const [name, setName] = useState(config?.server_display_name || "IPTV Bridge");
  const [format, setFormat] = useState(config?.stream_format || "ts");
  const [saving, setSaving] = useState(false);

  const [testUser, setTestUser] = useState("");
  const [testPass, setTestPass] = useState("");
  const [testing, setTesting] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/config", {
        xtream_url: url.trim(),
        server_display_name: name.trim(),
        stream_format: format,
      });
      toast.success("Configuration saved");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/test-connection", {
        email: testUser.trim(),
        password: testPass,
      });
      toast.success(`Connected — ${data.channels} channels loaded`);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  const inputCls =
    "w-full rounded-sm border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 outline-none transition-colors duration-200 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/50";
  const labelCls = "mb-1.5 block font-mono text-xs uppercase tracking-wider text-zinc-500";

  return (
    <div className="rounded-sm border border-zinc-800 bg-zinc-900 p-6" data-testid="config-panel">
      <div className="mb-5 flex items-center gap-2">
        <Link2 className="h-5 w-5 text-emerald-400" />
        <h2 className="font-heading text-lg font-semibold tracking-tight text-zinc-100">Xtream Panel</h2>
      </div>

      <div className="space-y-4">
        <div>
          <label className={labelCls}>Panel URL (host:port)</label>
          <input
            data-testid="xtream-url-input"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="http://panel.example.com:8080"
            className={`${inputCls} font-mono`}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Server Display Name</label>
            <input
              data-testid="server-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Stream Format</label>
            <div className="flex gap-2">
              {["ts", "m3u8"].map((f) => (
                <button
                  key={f}
                  data-testid={`format-${f}`}
                  onClick={() => setFormat(f)}
                  className={`flex-1 rounded-sm border px-3 py-2 font-mono text-xs uppercase tracking-wider transition-colors duration-200 ${
                    format === f
                      ? "border-emerald-500 bg-emerald-500/10 text-emerald-400"
                      : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
        </div>

        <button
          data-testid="save-config"
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 rounded-sm bg-emerald-600 px-4 py-2 font-medium text-white transition-colors duration-200 hover:bg-emerald-500 disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save Configuration
        </button>

        <div className="mt-2 border-t border-zinc-800 pt-4">
          <p className="mb-3 font-mono text-xs uppercase tracking-wider text-zinc-500">
            Test with Xtream credentials
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <input
              data-testid="test-username"
              value={testUser}
              onChange={(e) => setTestUser(e.target.value)}
              placeholder="xtream username"
              className={`${inputCls} font-mono`}
            />
            <input
              data-testid="test-password"
              type="password"
              value={testPass}
              onChange={(e) => setTestPass(e.target.value)}
              placeholder="xtream password"
              className={inputCls}
            />
          </div>
          <button
            data-testid="test-connection"
            onClick={test}
            disabled={testing || !testUser}
            className="mt-3 flex items-center gap-2 rounded-sm border border-zinc-700 bg-zinc-800 px-4 py-2 font-medium text-zinc-100 transition-colors duration-200 hover:bg-zinc-700 disabled:opacity-60"
          >
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />}
            Test Connection
          </button>
        </div>
      </div>
    </div>
  );
}
